import { Link } from 'gatsby';
import React, { ReactElement, useEffect, useRef } from 'react';
import VanillaTilt from 'vanilla-tilt';
import { twMerge } from 'tailwind-merge';
import { Headline } from '../../components';
import { formatUrl } from '../../utilities/';

interface IProps {
  title: string;
  headerBgColor: string;
  headerTextColor: string;
  headerIllo: string;
}

// Options that used to live on the element as data-tilt-* attributes. They are
// passed to init() instead — see the note in the effect below.
const TILT_OPTIONS = {
  glare: true,
  'max-glare': 0.35,
  speed: 1000,
  max: 10,
  scale: 1.03,
  perspective: 1500,
};

// The fancy card displayed for a library collection containing a title
// and an illo image
export default function CollectionsCard({
  title,
  headerBgColor = 'blue',
  headerTextColor = 'white',
  headerIllo,
}: IProps): ReactElement {
  const cardRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const element = cardRef.current;
    if (!element) return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!mediaQuery || mediaQuery.matches) return;

    VanillaTilt.init(element, TILT_OPTIONS);

    return () => {
      // @ts-ignore — vanillaTilt is attached to the element by init()
      element.vanillaTilt?.destroy();
    };
  }, []);

  return (
    // No data-tilt* attributes here on purpose. vanilla-tilt auto-initialises
    // at module scope — `VanillaTilt.init(document.querySelectorAll("[data-tilt]"))`
    // runs as soon as the chunk loads, which is before React hydrates. It would
    // inject a .js-tilt-glare child and rewrite this element's inline transform
    // out from under the server-rendered HTML, so hydration would fail and React
    // would throw away and re-render the whole page tree.
    //
    // Keeping the options in JS means the auto-init matches nothing and the
    // effect below owns initialisation — which also makes the reduced-motion
    // check actually take effect. It never did before: the auto-init got there
    // first, and init() no-ops on an element it has already set up.
    <Link
      // Gatsby types Link's ref as `Ref<Link> & Ref<HTMLAnchorElement>`, which a
      // plain element ref can't satisfy. It renders an <a>, so the cast is safe.
      ref={cardRef as React.Ref<any>}
      to={`/library/collections/${formatUrl(title)}/`}
      className={twMerge(
        'collections-card js-tilt relative h-87.5 overflow-hidden rounded-lg border border-transparent p-8 no-underline will-change-transform hover:border-neutral-gray-100 focus-visible:border-neutral-gray-100',
        `bg-base-${headerBgColor}`
      )}
      style={{
        transform:
          'perspective(500px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)',
        transformStyle: 'preserve-3d',
      }}>
      <Headline
        level="4"
        className={twMerge(
          `text-type-${headerTextColor}`,
          'relative z-10 block'
        )}
        style={{ transform: 'translateZ(20px)' }}>
        {title}
      </Headline>
      <img
        className="collections-card__image width-[261px] absolute bottom-0 left-0"
        loading="lazy"
        alt=""
        src={headerIllo}
      />
    </Link>
  );
}
